import React, { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  DEFAULT_CONTENT_LOCALE,
  useTheme,
  useAccountConfig,
  useAccountConfigStatus,
  type LocaleOption,
} from '@course-authoring/shared-types';
import { i18n, useTranslation } from '@course-authoring/i18n';

import { useAudioControls } from './AudioControlsContext';
import { GlobeIcon } from './GlobeIcon';
import {
  getUnrecognizedAccountLocales,
  resolveLanguagePanelOptions,
} from './languagePanelOptions';

type PanelKey = 'profile' | 'language';
type ButtonKey = PanelKey | 'audio';

const ICON_BUTTON_SIZE = 36;
// Inline (collapsed top-bar) circles are sized independently of the floating
// ones, with the glyphs scaled down to keep roughly the original ~56% fill
// ratio.
const INLINE_ICON_BUTTON_SIZE = 30;
const INLINE_ICON_SCALE = 0.82;
const PROFILE_PANEL_WIDTH = 284;
const LANGUAGE_PANEL_WIDTH = 214;
const PANEL_GAP = 8;
// Inline (collapsed top-bar) popovers sit closer to the buttons than the
// floating ones.
const INLINE_PANEL_GAP = 6;
const TOP_OFFSET = 16;
const RIGHT_OFFSET = 16;
const LIST_LINE_HEIGHT = '32px';
const POPOVER_SHADOW = '0px 2px 6px 2px rgba(0,0,0,0.08)';
const POPOVER_RADIUS = '5px';

const STUB_USER_NAME = 'Jamie Davis';
const STUB_USER_EMAIL = 'jamie.davis@company.com';

const MUTE_TOOLTIP_WIDTH = 220;

export interface UserToolbarProps {
  /**
   * 'floating' (default): absolute top-right vertical stack, panels open to the
   * left. 'inline': horizontal row placed by the parent (e.g. a collapsed top
   * bar), panels open downward.
   */
  variant?: 'floating' | 'inline';
  selectedLocale?: string;
  onLanguageSelect?: (locale: string) => void;
  learnerName?: string;
  learnerEmail?: string;
  /** Controlled tracking state. `true` ↔ learner has NOT opted out (users.noConsent = false). */
  trackingEnabled?: boolean;
  onTrackingChange?: (enabled: boolean) => void;
  /**
   * NA-408 "Show Block IDs" toggle. Ephemeral, session-only state owned by the
   * host (resets on every course launch, never persisted) — mirrors how
   * `trackingEnabled`/`onTrackingChange` are controlled from outside.
   */
  showBlockIds?: boolean;
  onShowBlockIdsChange?: (enabled: boolean) => void;
  /**
   * Account-level tracking disable. Overrides the account config's
   * `disableTracking` when provided. When true the toggle renders off and
   * read-only — the learner cannot opt back in.
   */
  trackingLocked?: boolean;
  /**
   * Preview surfaces (authoring) render a placeholder persona when identity is
   * absent; real players hide the identity rows instead of showing a stub.
   */
  isPreview?: boolean;
}

export function UserToolbar({
  variant = 'floating',
  selectedLocale,
  onLanguageSelect,
  learnerName,
  learnerEmail,
  trackingEnabled,
  onTrackingChange,
  trackingLocked,
  showBlockIds,
  onShowBlockIdsChange,
  isPreview = false,
}: UserToolbarProps): React.ReactElement {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const { disableTracking, selectedLanguages: accountSelectedLanguages } = useAccountConfig();
  const accountConfigStatus = useAccountConfigStatus();
  const effectiveTrackingLocked = trackingLocked ?? disableTracking;
  const isInline = variant === 'inline';
  const containerRef = useRef<HTMLDivElement>(null);
  const { audioAvailable, audioOn, toggleAudio, openPanel, setOpenPanel } = useAudioControls();
  const isToolbarPanelOpen = openPanel === 'profile' || openPanel === 'language';
  const [hoveredButton, setHoveredButton] = useState<ButtonKey | null>(null);
  const [hoveredLanguage, setHoveredLanguage] = useState<string | null>(null);
  const [internalSelectedLocale, setInternalSelectedLocale] = useState<string>(
    () => i18n.language?.toLowerCase() ?? DEFAULT_CONTENT_LOCALE,
  );
  // Controlled when the host provides trackingEnabled (mirrors activeLocale
  // below); internal state only drives the uncontrolled, cosmetic-only case.
  const [internalTrackingOn, setInternalTrackingOn] = useState(true);
  const trackingOn = effectiveTrackingLocked ? false : trackingEnabled ?? internalTrackingOn;
  const [trackingExpanded, setTrackingExpanded] = useState(false);
  // NA-408: default off, uncontrolled fallback for callers that don't wire it.
  const [internalShowBlockIds, setInternalShowBlockIds] = useState(false);
  const showBlockIdsOn = showBlockIds ?? internalShowBlockIds;

  // NA-242: learner toolbar chrome uses the fixed UI accent — never the
  // account accent.
  const accentColor = tokens.ui.fixedAccent;
  const languageOptions = useMemo(
    () => resolveLanguagePanelOptions({ accountSelectedLanguages }),
    [accountSelectedLanguages],
  );
  const unrecognizedAccountLocales = useMemo(
    () => getUnrecognizedAccountLocales(accountSelectedLanguages),
    [accountSelectedLanguages],
  );
  const languageSelectorDisabled = accountConfigStatus === 'error';
  const activeLocale = selectedLocale ?? internalSelectedLocale;

  useEffect(() => {
    for (const locale of unrecognizedAccountLocales) {
      console.warn(
        `[Language Selector] Unrecognized account locale "${locale}" was provided and will be ignored.`,
      );
    }
  }, [unrecognizedAccountLocales]);

  useEffect(() => {
    if (languageSelectorDisabled && openPanel === 'language') setOpenPanel(null);
  }, [languageSelectorDisabled, openPanel, setOpenPanel]);

  useEffect(() => {
    if (!isToolbarPanelOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpenPanel(null);
        setTrackingExpanded(false);
      }
    };
    window.addEventListener('mousedown', handlePointerDown);
    return () => window.removeEventListener('mousedown', handlePointerDown);
  }, [isToolbarPanelOpen, setOpenPanel]);

  useEffect(() => {
    if (!isToolbarPanelOpen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenPanel(null);
        setTrackingExpanded(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isToolbarPanelOpen, setOpenPanel]);

  const togglePanel = (panel: PanelKey) => {
    setOpenPanel(openPanel === panel ? null : panel);
    if (panel !== 'profile') setTrackingExpanded(false);
  };

  const container: CSSProperties = isInline
    ? {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: tokens.spacing.md,
      fontFamily: tokens.typography.fontFamily,
    }
    : {
      position: 'absolute',
      top: TOP_OFFSET,
      right: RIGHT_OFFSET,
      zIndex: 90,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      fontFamily: tokens.typography.fontFamily,
    };

  const renderIconButton = (
    key: ButtonKey,
    glyph: React.ReactNode,
    ariaLabel: string,
    onClick?: () => void,
    disabled = false,
  ): React.ReactElement => {
    const isActive = key !== 'audio' && openPanel === key;
    const isHovered = hoveredButton === key && !disabled;
    const buttonSize = isInline ? INLINE_ICON_BUTTON_SIZE : ICON_BUTTON_SIZE;
    const buttonStyle: CSSProperties = {
      boxSizing: 'border-box',
      width: `${buttonSize}px`,
      height: `${buttonSize}px`,
      borderRadius: tokens.radius.full,
      border: isInline
        ? 'none'
        : `1px solid ${isActive ? tokens.text.primary : tokens.borders.default}`,
      backgroundColor: isActive
        ? '#84848A'
        : isHovered
          ? '#DBDBDB'
          : tokens.backgrounds.page,
      color: isActive ? tokens.text.onBrand : tokens.text.primary,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      padding: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 150ms ease, color 150ms ease, border-color 150ms ease',
    };

    return (
      <button
        type="button"
        {...(key === 'audio' ? { 'data-narration-control': 'true' } : {})}
        style={buttonStyle}
        aria-label={ariaLabel}
        aria-pressed={key === 'audio' ? audioOn : isActive}
        disabled={disabled}
        onClick={onClick}
        onMouseEnter={key === 'audio' ? undefined : () => setHoveredButton(key)}
        onMouseLeave={key === 'audio'
          ? undefined
          : () => setHoveredButton((current) => (current === key ? null : current))}
      >
        {isInline ? (
          <span style={{ display: 'flex', transform: `scale(${INLINE_ICON_SCALE})` }}>
            {glyph}
          </span>
        ) : (
          glyph
        )}
      </button>
    );
  };

  const panelBase: CSSProperties = {
    position: 'absolute',
    // Inline: open downward, right-aligned to the button so wide panels stay
    // on-screen. Floating: open to the left of the vertical stack.
    ...(isInline
      ? { top: `calc(100% + ${INLINE_PANEL_GAP}px)`, right: 0 }
      : { top: 0, right: `calc(100% + ${PANEL_GAP}px)` }),
    boxSizing: 'border-box',
    backgroundColor: tokens.backgrounds.page,
    border: `1px solid ${tokens.borders.default}`,
    borderRadius: POPOVER_RADIUS,
    boxShadow: POPOVER_SHADOW,
    fontFamily: tokens.typography.fontFamily,
    color: tokens.text.primary,
  };

  const profilePanel: CSSProperties = {
    ...panelBase,
    width: `${PROFILE_PANEL_WIDTH}px`,
    padding: `${tokens.spacing.md} ${tokens.spacing.lgPlus}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing.lg,
  };

  const profileNameRow: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    fontSize: tokens.typography.sizes.base,
    lineHeight: tokens.typography.lineHeights.normal,
  };

  const profileDivider: CSSProperties = {
    border: 'none',
    borderTop: `1px solid ${tokens.borders.default}`,
    margin: 0,
  };

  const trackingRow: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.sm,
    fontSize: tokens.typography.sizes.base,
    lineHeight: tokens.typography.lineHeights.normal,
  };

  const trackingLabel: CSSProperties = {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacing.sm,
    fontWeight: tokens.typography.weights.bold,
  };

  const trackingLink: CSSProperties = {
    background: 'none',
    border: 'none',
    padding: 0,
    color: accentColor,
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: tokens.typography.weights.regular,
    cursor: 'pointer',
  };

  const trackingExplainer: CSSProperties = {
    margin: 0,
    fontSize: tokens.typography.sizes.base,
    lineHeight: tokens.typography.lineHeights.normal,
    color: tokens.text.primary,
  };

  const languagePanel: CSSProperties = {
    ...panelBase,
    width: 'max-content',
    minWidth: `${LANGUAGE_PANEL_WIDTH}px`,
    maxHeight: '310px',
    overflowY: 'auto',
    padding: '13px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  };

  const renderLanguageItem = (language: LocaleOption): React.ReactElement => {
    const isActive = activeLocale === language.locale;
    const isHovered = hoveredLanguage === language.locale && !isActive;
    const itemStyle: CSSProperties = {
      background: 'none',
      border: 'none',
      padding: 0,
      textAlign: 'left',
      fontFamily: 'inherit',
      fontSize: tokens.typography.sizes.base,
      lineHeight: LIST_LINE_HEIGHT,
      fontWeight: isActive ? tokens.typography.weights.bold : tokens.typography.weights.regular,
      color: isActive || isHovered ? accentColor : tokens.text.primary,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      direction: 'ltr',
    };
    return (
      <button
        key={language.locale}
        type="button"
        style={itemStyle}
        onClick={() => {
          setInternalSelectedLocale(language.locale);
          onLanguageSelect?.(language.locale);
          setOpenPanel(null);
        }}
        onMouseEnter={() => setHoveredLanguage(language.locale)}
        onMouseLeave={() =>
          setHoveredLanguage((current) => (current === language.locale ? null : current))
        }
      >
        {language.label}
      </button>
    );
  };

  return (
    <div ref={containerRef} style={container}>
      <div style={{ position: 'relative' }}>
        {renderIconButton(
          'profile',
          <PersonIcon />,
          openPanel === 'profile'
            ? t('learner_toolbar.profile_close')
            : t('learner_toolbar.profile_open'),
          () => togglePanel('profile'),
        )}
        {openPanel === 'profile' && (
          <div style={profilePanel} role="dialog" aria-label={t('learner_toolbar.profile_title')}>
            {(() => {
              const displayName = learnerName ?? (isPreview ? STUB_USER_NAME : undefined);
              const displayEmail = learnerEmail ?? (isPreview ? STUB_USER_EMAIL : undefined);
              if (!displayName && !displayEmail) return null;
              return (
                <>
                  <div style={profileNameRow}>
                    {displayName && (
                      <span style={{ fontWeight: tokens.typography.weights.bold }}>
                        {displayName}
                      </span>
                    )}
                    {displayEmail && (
                      <span style={{ fontWeight: tokens.typography.weights.regular }}>
                        {displayEmail}
                      </span>
                    )}
                  </div>
                  <hr style={profileDivider} />
                </>
              );
            })()}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: tokens.spacing.lg,
              }}
            >
              <div style={trackingRow}>
                <span style={trackingLabel}>
                  <span>{t('learner_toolbar.show_block_ids')}</span>
                </span>
                <TrackingToggle
                  on={showBlockIdsOn}
                  onToggle={() => {
                    const next = !showBlockIdsOn;
                    if (showBlockIds === undefined) setInternalShowBlockIds(next);
                    onShowBlockIdsChange?.(next);
                  }}
                  accentColor={accentColor}
                  offColor={tokens.borders.default}
                  knobColor={tokens.text.onBrand}
                  ariaLabel={t('learner_toolbar.show_block_ids')}
                />
              </div>
              <div style={trackingRow}>
                <span style={trackingLabel}>
                  <span>{t('learner_toolbar.tracking')}</span>
                  <button
                    type="button"
                    style={trackingLink}
                    onClick={() => setTrackingExpanded((value) => !value)}
                    aria-expanded={trackingExpanded}
                  >
                    {trackingExpanded
                      ? t('learner_toolbar.show_less')
                      : t('learner_toolbar.what_is_this')}
                  </button>
                </span>
                <TrackingToggle
                  on={trackingOn}
                  disabled={effectiveTrackingLocked}
                  onToggle={() => {
                    if (effectiveTrackingLocked) return;
                    const next = !trackingOn;
                    if (trackingEnabled === undefined) setInternalTrackingOn(next);
                    onTrackingChange?.(next);
                  }}
                  accentColor={accentColor}
                  offColor={tokens.borders.default}
                  knobColor={tokens.text.onBrand}
                  ariaLabel={t('learner_toolbar.tracking')}
                />
              </div>
              {trackingExpanded && (
                <p style={trackingExplainer}>{t('learner_toolbar.tracking_explainer')}</p>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={{ position: 'relative' }}>
        {renderIconButton(
          'language',
          <GlobeIcon />,
          openPanel === 'language'
            ? t('questionnaire.language_close')
            : t('questionnaire.language_open'),
          () => togglePanel('language'),
          languageSelectorDisabled,
        )}
        {openPanel === 'language' && (
          <div style={languagePanel} role="dialog" aria-label={t('questionnaire.language_title')}>
            {languageOptions.map(renderLanguageItem)}
          </div>
        )}
      </div>

      <div
        style={{ position: 'relative' }}
        onMouseEnter={() => setHoveredButton('audio')}
        onMouseLeave={() => setHoveredButton((current) => (current === 'audio' ? null : current))}
      >
        {renderIconButton(
          'audio',
          audioOn ? <UnmutedIcon /> : <MuteIcon />,
          audioAvailable
            ? (audioOn
                ? t('learner_toolbar.audio_turn_off')
                : t('learner_toolbar.audio_turn_on'))
            : t('learner_toolbar.audio_unavailable'),
          toggleAudio,
          !audioAvailable,
        )}
        {!audioOn && hoveredButton === 'audio' && openPanel === null && (
          <div
            role="tooltip"
            style={{
              ...panelBase,
              width: `${MUTE_TOOLTIP_WIDTH}px`,
              padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
              fontSize: tokens.typography.sizes.base,
              lineHeight: tokens.typography.lineHeights.uiSoft,
              pointerEvents: 'none',
            }}
          >
            {audioAvailable
              ? t('learner_toolbar.audio_description')
              : t('learner_toolbar.audio_unavailable')}
          </div>
        )}
      </div>
    </div>
  );
}

UserToolbar.displayName = 'UserToolbar';

function TrackingToggle({
  on,
  disabled = false,
  onToggle,
  accentColor,
  offColor,
  knobColor,
  ariaLabel,
}: {
  on: boolean;
  disabled?: boolean;
  onToggle: () => void;
  accentColor: string;
  offColor: string;
  knobColor: string;
  ariaLabel: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onToggle}
      style={{
        position: 'relative',
        width: '32px',
        height: '16px',
        borderRadius: '999px',
        backgroundColor: on ? accentColor : offColor,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        padding: 0,
        transition: 'background-color 150ms ease',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '2px',
          left: on ? '18px' : '2px',
          width: '12px',
          height: '12px',
          borderRadius: '999px',
          backgroundColor: knobColor,
          transition: 'left 150ms ease',
        }}
      />
    </button>
  );
}

function PersonIcon(): React.ReactElement {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  );
}

function MuteIcon(): React.ReactElement {
  return (
    <svg
      width="22"
      height="20"
      viewBox="0 0 24 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M3 7v6h4l5 5V2L7 7H3z" />
      <path
        d="M16.5 6.5l5 5M21.5 6.5l-5 5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function UnmutedIcon(): React.ReactElement {
  return (
    <svg
      width="22"
      height="20"
      viewBox="0 0 24 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M3 7v6h4l5 5V2L7 7H3z" />
      <path
        d="M16 7a4 4 0 010 6M19 5a6 6 0 010 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
